import { generatePath, Link } from 'react-router-dom'

import { MultisigProposal } from '@/api'
import ProposalStatusBadge from '@/common/ProposalStatusBadge'
import { RoutePaths } from '@/routes'
import { UiCard } from '@/ui/UiCard'

interface ProposalCardProps {
  proposal: MultisigProposal
}

export default function ProposalCard({ proposal }: ProposalCardProps) {
  const proposalDetailsPath = generatePath(RoutePaths.MultisigGroupProposal, {
    account: proposal.group,
    proposalId: proposal.id,
  })

  const yesCount = proposal.finalTallyResult?.yesCount
  const noCount = proposal.finalTallyResult?.noCount
  const isVotingActive = proposal.status === 'SUBMITTED'

  return (
    <Link to={proposalDetailsPath} className='group block'>
      <UiCard className='hover:border-border/90 hover:bg-card/45 relative overflow-hidden transition-all'>
        {isVotingActive && (
          <>
            <div className='bg-accent absolute top-0 left-0 h-full w-1' />
            <div className='bg-accent/20 group-hover:bg-accent/30 absolute top-0 left-0 h-full w-full transition-colors' />
          </>
        )}
        <div className='relative flex flex-wrap items-center gap-3 p-6'>
          <div className='order-1 flex min-w-0 items-center gap-2'>
            <p className='font-mono text-base font-semibold'>
              Proposal #{proposal.id}
            </p>
            {isVotingActive && (
              <span className='bg-accent/80 relative flex h-2 w-2 rounded-full'>
                <span className='bg-accent absolute inline-flex h-full w-full animate-ping rounded-full opacity-75' />
              </span>
            )}
          </div>

          <div className='order-2 ml-auto shrink-0 sm:order-3'>
            <ProposalStatusBadge status={proposal.status} />
          </div>

          <div className='order-3 w-full sm:order-2 sm:flex sm:flex-1 sm:justify-center'>
            <div className='flex flex-wrap items-start gap-x-10 gap-y-3'>
              <div className='flex min-w-40 flex-col gap-1 text-sm'>
                <span className='text-muted-foreground'>Voting end block:</span>
                <span className='text-foreground font-medium'>
                  {proposal.votingEndBlock}
                </span>
              </div>

              <div className='flex min-w-30 flex-col gap-1 text-sm'>
                <span className='text-muted-foreground'>Messages:</span>
                <span className='text-foreground font-medium'>
                  {proposal.messages.length}
                </span>
              </div>

              <div className='flex min-w-30 flex-col gap-1 text-sm'>
                <span className='text-muted-foreground'>YES:</span>
                <span className='text-foreground'>{yesCount ?? '—'}</span>
              </div>

              <div className='flex min-w-30 flex-col gap-1 text-sm'>
                <span className='text-muted-foreground'>NO:</span>
                <span className='text-foreground'>{noCount ?? '—'}</span>
              </div>
            </div>
          </div>
        </div>
      </UiCard>
    </Link>
  )
}
